import { supabase } from './supabase';

/**
 * Abonnement PUSH de la TABLETTE (caisse) — son cuisine fiable même écran éteint.
 * La tablette s'abonne aux notifications web push ; l'abonnement est stocké dans
 * `push_subscriptions` SANS `customer_id` (= appareil de service), avec le tenant_id.
 * Le déclencheur `stripe-webhook` (commande payée) envoie alors un push à ces
 * abonnements via `send-push`.
 *
 * VAPID : même clé publique que le serveur (send-push) — clé publique, non secrète.
 */
export const VAPID_PUBLIC_KEY =
  'BNippvm_g2Mtvh9jcFNWwIWom37EtUN2BEg_hXm7hTWCk513BUt_NpjNf5hY4ewlv6g_ooeXzBnkjxJtuPz2R_w';

export type TabletPushStatus =
  | 'unsupported'
  | 'denied'
  | 'subscribed'
  | 'error';

/** Le navigateur supporte-t-il le web push ? */
export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function bufToBase64(buf: ArrayBuffer | null): string {
  if (!buf) return '';
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.byteLength; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

/**
 * Abonne la tablette aux notifications push pour ce resto.
 * Demande la permission (geste utilisateur requis) → enregistre le SW →
 * souscrit au PushManager → stocke via la RPC `register_push_subscription`
 * (même signature que le legacy : `p_tenant_slug` + `p_customer_email = null`
 * → abonnement « appareil de service » = customer_id NULL côté base).
 */
export async function subscribeTabletPush(tenantSlug: string): Promise<TabletPushStatus> {
  if (!pushSupported()) return 'unsupported';
  try {
    const reg = await navigator.serviceWorker.register('/sw-push.js');
    await navigator.serviceWorker.ready;

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return 'denied';

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }

    const { data, error } = await supabase.rpc('register_push_subscription', {
      p_tenant_slug: tenantSlug,
      p_endpoint: sub.endpoint,
      p_p256dh_key: bufToBase64(sub.getKey('p256dh')),
      p_auth_key: bufToBase64(sub.getKey('auth')),
      p_user_agent: navigator.userAgent.slice(0, 200),
      p_platform: 'web',
      p_customer_email: null,
    });
    const ok = (data as { ok?: boolean } | null)?.ok;
    if (error || ok === false) {
      console.warn('[tabletPush] enregistrement abonnement échoué:', error?.message);
      return 'error';
    }
    return 'subscribed';
  } catch (e) {
    console.warn('[tabletPush] abonnement impossible:', (e as Error).message);
    return 'error';
  }
}

/** Indique si la tablette est déjà abonnée (permission accordée + souscription active). */
export async function tabletPushActive(): Promise<boolean> {
  if (!pushSupported()) return false;
  try {
    if (Notification.permission !== 'granted') return false;
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
}
